import { useRef, useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api } from "@/lib/api";
import { SIN_ORDEN, validarNombre, type NodoCategoria, type NodoSubcategoria, type OrdenArbol } from "@/lib/cotizaciones";

type Que = "categoria" | "subcategoria";
type Dialogo =
  | { tipo: "nombre"; que: "categoria"; id: number | null }
  | { tipo: "nombre"; que: "subcategoria"; id: number | null; categoriaId: number }
  | { tipo: "borrar"; que: Que; id: number; nombre: string }
  | null;

interface Opciones {
  /** Después de cada cambio guardado: volver a pedir el árbol y los presupuestos (cambian los números). */
  alCambiar: () => void;
}

/**
 * Edición del árbol de Plata → Presupuestos (sin JSX; Mati, 2026-10-30): agregar, renombrar y borrar categorías
 * (rubros) y subcategorías (ítems del presupuesto de obra), y moverlas con `ListaOrdenable`. Cada movida se ve al toque
 * (`orden`, que `armarArbol` aplica y renumera) y se guarda en cola, como en Obra → Presupuesto. Una categoría con
 * subcategorías no se borra (primero se borran ellas); una subcategoría con presupuestos tampoco (el backend da 409 y
 * se muestra su mensaje). Escribe sólo el admin. La UI está en `EdicionCategorias.tsx`.
 */
export function useEdicionCategorias({ alCambiar }: Opciones) {
  const { fid } = useContextoApp();
  const base = `/api/fideicomisos/${fid}`;

  const [editando, setEditando] = useState(false);
  const [orden, setOrden] = useState<OrdenArbol>(SIN_ORDEN);
  const [errorOrden, setErrorOrden] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [nombre, setNombre] = useState("");
  const [mostrarError, setMostrarError] = useState(false);

  const cola = useRef<Promise<unknown>>(Promise.resolve());
  const guardarOrden = (ruta: string, ids: number[], siFalla: () => void) => {
    setErrorOrden(null);
    cola.current = cola.current.then(() =>
      api.put<number[]>(ruta, { ids }).then(alCambiar, (e: unknown) => {
        setErrorOrden(e instanceof Error ? e.message : "No se pudo guardar el orden");
        siFalla();
      }),
    );
  };

  const accionGuardar = useAsyncAction((d: Exclude<Dialogo, null | { tipo: "borrar" }>, texto: string) => {
    if (d.que === "categoria") return d.id === null ? api.post(`${base}/rubros`, { nombre: texto }) : api.patch(`${base}/rubros/${d.id}`, { nombre: texto });
    return d.id === null
      ? api.post(`${base}/presupuesto/rubros/${d.categoriaId}/items`, { descripcion: texto })
      : api.patch(`${base}/presupuesto/items/${d.id}`, { descripcion: texto });
  });
  const accionBorrar = useAsyncAction((que: Que, id: number) =>
    que === "categoria"
      ? api.delete<{ resultado: "borrado" | "archivado" }>(`${base}/rubros/${id}`).then((r) => r.resultado)
      : api.delete<void>(`${base}/presupuesto/items/${id}`).then(() => "borrado" as const),
  );

  const queNombre = dialogo?.tipo === "nombre" ? dialogo.que : "categoria";
  const error = validarNombre(nombre, queNombre);
  const cerrar = () => setDialogo(null);

  const abrirNombre = (d: Exclude<Dialogo, null | { tipo: "borrar" }>, actual = "") => {
    setNombre(actual);
    setMostrarError(false);
    accionGuardar.limpiarError();
    setAviso(null);
    setDialogo(d);
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarError(true);
    if (dialogo?.tipo !== "nombre" || error) return;
    if ((await accionGuardar.run(dialogo, nombre.trim())) !== undefined) {
      cerrar();
      alCambiar();
    }
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    const resultado = await accionBorrar.run(dialogo.que, dialogo.id);
    if (!resultado) return;
    if (resultado === "archivado") setAviso(`«${dialogo.nombre}» tiene gastos cargados: quedó archivada en vez de borrarse.`);
    cerrar();
    alCambiar();
  };

  return {
    editando,
    alternarEdicion: () => {
      setEditando((e) => !e);
      setAviso(null);
      setErrorOrden(null);
    },
    orden,
    errorOrden,
    aviso,
    moverCategorias: (ids: number[]) => {
      setOrden((o) => ({ ...o, categorias: ids }));
      guardarOrden(`${base}/presupuesto/orden`, ids, () => setOrden((o) => ({ ...o, categorias: null })));
    },
    moverSubcategorias: (categoriaId: number, ids: number[]) => {
      setOrden((o) => ({ ...o, subcategorias: { ...o.subcategorias, [categoriaId]: ids } }));
      guardarOrden(`${base}/presupuesto/rubros/${categoriaId}/orden`, ids, () =>
        setOrden((o) => {
          const { [categoriaId]: _, ...resto } = o.subcategorias;
          return { ...o, subcategorias: resto };
        }),
      );
    },
    // diálogos
    dialogo,
    cerrar,
    abrirNuevaCategoria: () => abrirNombre({ tipo: "nombre", que: "categoria", id: null }),
    abrirRenombrarCategoria: (c: NodoCategoria) => abrirNombre({ tipo: "nombre", que: "categoria", id: c.id }, c.nombre),
    abrirNuevaSubcategoria: (c: NodoCategoria) => abrirNombre({ tipo: "nombre", que: "subcategoria", id: null, categoriaId: c.id }),
    abrirRenombrarSubcategoria: (c: NodoCategoria, s: NodoSubcategoria) => abrirNombre({ tipo: "nombre", que: "subcategoria", id: s.id, categoriaId: c.id }, s.descripcion),
    abrirBorrarCategoria: (c: NodoCategoria) => {
      accionBorrar.limpiarError();
      setAviso(null);
      setDialogo({ tipo: "borrar", que: "categoria", id: c.id, nombre: c.nombre });
    },
    abrirBorrarSubcategoria: (s: NodoSubcategoria) => {
      accionBorrar.limpiarError();
      setAviso(null);
      setDialogo({ tipo: "borrar", que: "subcategoria", id: s.id, nombre: `${s.numero} ${s.descripcion}` });
    },
    tituloNombre:
      dialogo?.tipo !== "nombre" ? "" : dialogo.que === "categoria" ? (dialogo.id === null ? "Nueva categoría" : "Renombrar categoría") : dialogo.id === null ? "Nueva subcategoría" : "Renombrar subcategoría",
    nombre,
    setNombre,
    errorNombre: mostrarError ? error : undefined,
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    mensajeBorrar:
      dialogo?.tipo !== "borrar"
        ? ""
        : dialogo.que === "categoria"
          ? `¿Borrar la categoría «${dialogo.nombre}»? Si tiene gastos cargados, queda archivada.`
          : `¿Borrar la subcategoría «${dialogo.nombre}»? También sale de Obra → Presupuesto y del cronograma.`,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}

export type EdicionCategorias = ReturnType<typeof useEdicionCategorias>;
